import { StatsContainer, DataTable, Button } from "@/components";
import { RightWrapper } from "../../../components";
import { useMemo, useCallback, useEffect, useState } from "react";
import { getCodRemittanceData } from "../../../utils/Data";
import { HiOutlineDownload } from "react-icons/hi";
import ColumnHelper from "@/utils/ColumnHelper";
import {
  fetchCodRemittance,
  selectAllCodRemittances,
} from "../../../app/slices";
import { useSelector, useDispatch } from "react-redux";
import { useApi } from "@/";
import { HiOutlineArrowDown } from "react-icons/hi";

const CodRemittance = () => {
  const { callApi, loading } = useApi();
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const fetchData = async () => {
    const response = await dispatch(fetchCodRemittance());
    console.log("response::::::", response);
  };
  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const codRemittances = useSelector(selectAllCodRemittances) || [];

  const isCodRemittancesLoading = useSelector(
    (state) => state.codRemittance.loading
  );

  const totalDue = useSelector((state) => state.codRemittance.totalDue);
  const totalRemittance = useSelector(
    (state) => state.codRemittance.totalRemittance
  );
  const lastRemittance = useSelector(
    (state) => state.codRemittance.lastRemittance
  );

  const statusData = [
    {
      statusName: "Total Remittance",
      statusNumber: parseFloat(totalRemittance) || 0,
    },
    {
      statusName: "Last Remittance",
      statusNumber: parseFloat(lastRemittance) || 0,
    },
    {
      statusName: "Next Remittance",
      statusNumber: (parseFloat(totalDue) || 0) * 0.3,
    },
    {
      statusName: "Total Remittance Due",
      statusNumber: parseFloat(totalDue) || 0,
    },
  ];

  // Add this import at the top with other icons

  // Add state for download loading at the top of component

  const handleDownload = async (remittanceId) => {
    console.log(remittanceId);
    setIsDownloading(true);
    try {
      const response = await callApi(
        "/common/export",
        "POST",
        {
          model: "User CODRemittance",
          ids: [remittanceId],
          filename: `Remittance-${remittanceId}-${new Date().getTime()}.csv`,
        },
        null,
        null,
        "blob"
      );

      // Check if response is JSON error
      const contentType = response?.headers?.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        console.error("Export failed:", errorData.message);
        return; // Exit early
      }

      // Proceed with blob handling if not JSON
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Remittance-${remittanceId}-${new Date().getTime()}.csv`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Add this component at the bottom of the return statement (before closing RightWrapper)
  {
    isDownloading && (
      <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-lg shadow-lg">
        <HiOutlineArrowDown className="animate-bounce" />
        <span className="font-nunito">Downloading remittance details...</span>
      </div>
    );
  }

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "S.NO",
        accessorKey: "sNo",
        cell: (info) => (
          <span className="font-nunito">{info.row.index + 1}</span>
        ),
        meta: {
          tooltip: (row) => row.sNo,
        },
        width: "50px",
      }),
      ColumnHelper({
        header: "Remittance",
        accessorKey: "remittanceId",
        meta: {
          tooltip: (row) => row.remittanceId,
        },
      }),
      ColumnHelper({
        header: "COD Amount",
        accessorKey: "totalPaidAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalPaidAmount)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalPaidAmount),
        },
      }),
      ColumnHelper({
        header: "Payment Date",
        accessorKey: "createdAt",
        cell: (info) => formatDate(info.row.original.createdAt),
        meta: {
          tooltip: (row) => formatDate(row.createdAt),
        },
      }),
      ColumnHelper({
        header: "Wallet Payment",
        accessorKey: "walletPayment",
        cell: (info) => (
          <span className="font-nunito">
            {info.row.original?.paymentMethod === "Wallet"
              ? formatCurrency(info.row.original.totalPaidAmount)
              : "---"}
          </span>
        ),
        meta: {
          tooltip: (row) => {
            return row?.paymentMethod === "Wallet"
              ? formatCurrency(row.totalPaidAmount)
              : "---";
          },
        },
      }),
      ColumnHelper({
        header: "Account Payment",
        accessorKey: "accountPayment",
        cell: (info) => (
          <span className="font-nunito">
            {info.row.original?.paymentMethod === "Bank Account"
              ? formatCurrency(info.row.original.totalPaidAmount)
              : "---"}
          </span>
        ),
        meta: {
          tooltip: (row) => {
            return row?.paymentMethod === "Bank Account"
              ? formatCurrency(row.totalPaidAmount)
              : "---";
          },
        },
      }),
      ColumnHelper({
        header: "UTR/Reference",
        accessorKey: "transactionId",
        meta: {
          tooltip: (row) => row.transactionId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Download",
        accessorKey: "id",
        cell: (info) => (
          <div className="flex gap-1 justify-center items-center">
            <Button
              size="xs"
              icon={HiOutlineDownload}
              onClick={() => handleDownload(info.row.original._id)}
            />
          </div>
        ),
        meta: {
          tooltip: () => "Download remittance details",
        },
      }),
    ],
    []
  );

  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <StatsContainer
          status={statusData}
          label="COD Remittance "
          lgCols={4}
        />
        <DataTable
          columns={columns}
          tittle="COD Remittance"
          data={codRemittances}
          showCheckbox={false}
          isLoading={isCodRemittancesLoading}
        />
      </div>
    </RightWrapper>
  );
};

export default CodRemittance;
